import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  ElementRef,
  EnvironmentInjector,
  HostListener,
  OnDestroy,
  Type,
  ViewChild,
  ViewContainerRef,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  inputBinding,
  output,
  signal,
  twoWayBinding,
  untracked,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faFilter,
  faMagnifyingGlass,
  faTimes,
  faChevronDown,
  faPlus,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { map } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth.service';
import { log } from '../../../../utils/logger';
import {
  FilterConfig,
  UnknownFilterDefinition,
} from '../../../../utils/filters/filter-definition';
import {
  FilterPreferences,
  FilterPreferencesService,
} from '../../../../services';
import { KeepInViewportDirective } from '../../keep-in-viewport.directive';

const logger = log.getLogger('filter-container.component');
let nextFilterContainerId = 0;

@Component({
  selector: 'app-filter-container',
  standalone: true,
  imports: [FormsModule, FaIconComponent, KeepInViewportDirective],
  templateUrl: './filter-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterContainerComponent implements OnDestroy {
  filter = input.required<UnknownFilterDefinition>();
  storageKey = input<string | undefined>(undefined);
  addFilterLabel = input<string | undefined>(undefined);
  additionalDropdownAlign = input<'left' | 'right'>('right');
  /**
   * Centered-overlay presentation for the More Filters dropdown (the
   * mobile treatment at every breakpoint). Use inside side panels /
   * drawers whose scrollable body would clip the default
   * absolute-positioned dropdown.
   */
  dropdownOverlay = input(false);
  clearLabel = input('Clear');
  clearAriaLabel = input('Clear all filters');
  hasExternalActiveFilters = input(false);
  cleared = output<void>();
  // These flags let pages prefer externally supplied filter state (for example,
  // URL/query-param handoffs) over saved local preferences during initial load.
  shouldUseIncomingFilterState = input(false);
  savePreferencesEnabled = input(true);
  shouldAvoidPersistingIncomingFilterState = input(false);
  /**
   * When set, only these filter names are rendered. Lets one definition
   * drive two toolbars (e.g. period at the top, team by the table).
   */
  visibleFilterNames = input<readonly string[] | undefined>(undefined);

  options: Record<string, WritableSignal<unknown>> = {};
  private readonly filterResetToken = signal(0);
  // Prefer optionsFetchEnabled when the filter separates "show filter UI"
  // (optionsEnabled, often read by the host page) from "fetch dropdown options"
  // (may wait on async scope proof). Fall back to optionsEnabled for all other
  // filters so existing behavior is unchanged.
  private readonly optionsQueryFetchEnabled = computed(() => {
    const filter = this.filter();
    return filter.optionsFetchEnabled?.() ?? filter.optionsEnabled();
  });

  optionsQuery = injectQuery(() => ({
    queryKey: this.filter().optionsQueryKey(),
    queryFn: () => this.filter().getOptions(),
    enabled: this.optionsQueryFetchEnabled(),
  }));

  // Template state
  protected readonly faMagnifyingGlass = faMagnifyingGlass;
  protected readonly faFilter = faFilter;
  protected readonly faTimes = faTimes;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faPlus = faPlus;
  protected readonly faCheck = faCheck;

  protected showAdditionalDropdown = signal(false);
  protected dropdownSearch = signal('');
  protected activeAdditionalFilters = signal<Set<string>>(new Set());
  protected readonly additionalSearchInputId = `filter-container-additional-search-${nextFilterContainerId++}`;

  protected filteredAdditionalFilters = computed(() => {
    const term = this.dropdownSearch().toLowerCase().trim();
    const available = this.getAvailableAdditionalFilters();
    if (!term) return available;
    return available.filter(c =>
      (c.title || c.name || '').toLowerCase().includes(term)
    );
  });

  // Filters that have a visible UI component and are not required —
  // used by hasAnyActiveFilter (to show "Clear") and clearAllFilters.
  private clearableFilterNames = computed(() => {
    return new Set(
      this.filter()
        .configs()
        .filter(
          c =>
            c.filterComponent &&
            c.location !== 'none' &&
            !c.required &&
            this.configIncluded(c.name)
        )
        .map(c => c.name)
    );
  });

  protected hasAnyActiveFilter = computed(() => {
    if (this.activeAdditionalFilters().size > 0) {
      return true;
    }
    const filter = this.filter();
    const names = this.visibleFilterNames();
    if (filter.hasActiveFilters) {
      if (!names) {
        return filter.hasActiveFilters();
      }
      const omitsClearable = filter
        .configs()
        .some(
          c =>
            c.filterComponent &&
            c.location !== 'none' &&
            !c.required &&
            !names.includes(c.name)
        );
      if (!omitsClearable) {
        return filter.hasActiveFilters();
      }
    }
    const values = filter.values;
    const clearable = this.clearableFilterNames();
    return Object.keys(values).some(key => {
      if (!clearable.has(key)) return false;
      const valueSignal = values[key];
      return valueSignal && this.hasActiveValue(valueSignal());
    });
  });

  protected allAdditionalFiltersActive = computed(() => {
    return (
      this.getAvailableAdditionalFilters().length === 0 &&
      this.activeAdditionalFilters().size > 0
    );
  });

  protected showAddFilterButton = computed(() => {
    if (this.getAvailableAdditionalFilters().length > 0) {
      return true;
    }
    // While options are loading — or the fetch gate is still false (e.g.
    // optionsFetchEnabled waiting on async scope proof) — keep the button
    // visible so it doesn't vanish and reappear once the query runs.
    if (this.optionsQueryPending()) {
      return this.getAdditionalFilterConfigs().length > 0;
    }
    return false;
  });

  @ViewChild('defaultFiltersContainer', {
    static: false,
    read: ViewContainerRef,
  })
  defaultFiltersContainer?: ViewContainerRef;

  @ViewChild('additionalFiltersContainer', {
    static: false,
    read: ViewContainerRef,
  })
  additionalFiltersContainer?: ViewContainerRef;

  @ViewChild('secondaryFiltersContainer', {
    static: false,
    read: ViewContainerRef,
  })
  secondaryFiltersContainer?: ViewContainerRef;

  @ViewChild('dropdownSearchInput')
  dropdownSearchInput?: ElementRef<HTMLInputElement>;

  @ViewChild('addFilterButton')
  addFilterButton?: ElementRef<HTMLButtonElement>;

  private readonly envInjector = inject(EnvironmentInjector);
  private readonly preferencesService = inject(FilterPreferencesService);
  private readonly authService = inject(AuthService);
  private readonly authUidForFilterPrefs = toSignal(
    this.authService.user$.pipe(map(u => u?.user?.uid ?? null)),
    { initialValue: this.authService.user?.uid ?? null }
  );
  private readonly authFilterContextForPrefs = toSignal(
    toObservable(this.authService.filterPreferencesContextKey),
    { initialValue: this.authService.filterPreferencesContextKey() }
  );
  private filterComponents: { [key: string]: Type<any> } = {};
  private componentRefs = new Map<string, ComponentRef<any>>();
  private pendingAutoOpen: string | null = null;
  private storageLoaded = signal(false);
  /** When this changes (e.g. login as a different user), preferences load again. */
  private lastRestoredSessionToken: string | null = null;
  private saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Kept after the first restore because filters can register later than the
   * container (agency custom-field chips load asynchronously). Their stored
   * values are applied once they exist and are preserved on save until then.
   */
  private restoredPreferences: FilterPreferences | null = null;
  private pendingRestoreValueNames = new Set<string>();
  private pendingRestoreActiveNames = new Set<string>();

  private pendingSaveSessionToken: string | null = null;
  private hasSkippedFirstSave = false;
  private skipNextAutoSave = false;

  private optionsQueryPending = computed(() => {
    if (!this.optionsQueryFetchEnabled()) {
      return true;
    }
    return this.optionsQuery.isLoading() || this.optionsQuery.isFetching();
  });

  private configIncluded(name: string): boolean {
    const names = this.visibleFilterNames();
    return names == null || names.includes(name);
  }

  private staticOptions(name: string): unknown {
    return this.filter()
      .configs()
      .find(config => config.name === name)?.['options'];
  }

  private visibleConfigs = computed(() => {
    const queryData = this.optionsQuery.data();
    const optionsPending = this.optionsQueryPending();

    return this.filter()
      .configs()
      .filter(config => {
        if (!this.configIncluded(config.name)) {
          return false;
        }
        const options = queryData?.[config.name];
        const isActiveAdditionalFilter =
          config.location === 'additional' &&
          this.activeAdditionalFilters().has(config.name);
        const filterValueSignal = this.filter().values[config.name];
        const hasValue = filterValueSignal
          ? this.hasActiveValue(filterValueSignal())
          : false;

        if (options) {
          // Keep actively-used filters visible even if current options are temporarily incompatible.
          return (
            (Array.isArray(options) && options.length > 0) ||
            config.showWhenOptionsEmpty === true ||
            isActiveAdditionalFilter ||
            hasValue
          );
        }

        // Options haven't loaded yet for this filter. While the fetch gate is
        // false or the query is in flight, hide additional filters so the
        // dropdown doesn't flash a long list that shrinks once results arrive.
        // Default-location filters (e.g. search) always render immediately.
        if (optionsPending && config.location === 'additional') {
          return (
            config.showWhenOptionsEmpty === true ||
            hasValue ||
            isActiveAdditionalFilter
          );
        }

        return true;
      });
  });

  constructor() {
    effect(() => {
      const options = this.optionsQuery.data();
      if (!options) {
        // TODO if components render at this stage, they won't get their options.
        return;
      }

      // Convert the options data into signals so that we can send updates to the components.
      // This allows us to update the components when the user changes their agency without
      // having to deal with ngOnInit and ngOnChanges.

      const existingKeys = Object.keys(this.options);

      for (const key of existingKeys) {
        if (!options?.[key]) {
          // Options-query keys often include filter values, so a refetch
          // follows each value change; keep options the config defines.
          this.options[key].set(untracked(() => this.staticOptions(key)) ?? []);
        } else {
          this.options[key].set(options[key]);
        }
      }

      for (const key of Object.keys(options)) {
        if (!this.options[key]) {
          this.options[key] = signal<unknown>(options[key]);
        }
      }

      logger.debug('Updated options', options, Object.keys(this.options));
    });

    // Persist all filter values and active additional filters to
    // localStorage so the user's selections survive page reloads.
    effect(() => {
      void this.authFilterContextForPrefs();
      const active = this.activeAdditionalFilters();
      const key = this.storageKey();
      if (!key || !this.storageLoaded() || !this.savePreferencesEnabled()) {
        this.clearPendingSaveDebounce();
        return;
      }

      const savedValues = this.collectPersistableValues();

      const activeNames = this.persistableActiveAdditionalFilters(active);
      this.clearPendingSaveDebounce();
      if (this.shouldSkipFirstAutoSave()) {
        return;
      }
      const sessionAtSchedule =
        this.preferencesService.preferencesSessionToken(key);
      if (!sessionAtSchedule) {
        return;
      }
      this.pendingSaveSessionToken = sessionAtSchedule;
      this.saveDebounceTimer = setTimeout(() => {
        if (
          this.preferencesService.preferencesSessionToken(key) !==
          sessionAtSchedule
        ) {
          this.pendingSaveSessionToken = null;
          return;
        }
        this.pendingSaveSessionToken = null;
        this.preferencesService.savePreferences(key, {
          values: savedValues,
          activeAdditionalFilters: activeNames,
        });
      }, 300);
    });

    effect(async () => {
      const visibleFilterConfigs = this.visibleConfigs();

      if (visibleFilterConfigs.length > 0) {
        await this.loadFilterComponents();
      }
      // Re-read after the await. Lazy component imports yield, and in that
      // window the user can add a filter (or its options can arrive), so a
      // pre-await snapshot would drop a filter that just became visible.
      this.cleanupInvisibleActiveFilters(
        new Set(untracked(() => this.visibleConfigs()).map(c => c.name))
      );
      this.renderFilterComponents();
    });

    // Preferences are keyed by uid + acting-as context; re-render when either changes.
    effect(() => {
      const key = this.storageKey();
      if (!key) return;
      const uid = this.authUidForFilterPrefs();
      if (!uid) return;
      void this.authFilterContextForPrefs();
      queueMicrotask(() => this.renderFilterComponents());
    });

    // Auto-activate additional filters that have values set (e.g., from presets or URL params).
    // skipAutoActivateWhenValuePresent filters only activate when hasActiveValue is true
    // (e.g. complete ConditionGroups), never for empty/incomplete drafts.
    effect(() => {
      const filter = this.filter();
      const additionalConfigs = filter
        .configs()
        .filter(c => c.location === 'additional');
      const active = new Set(this.activeAdditionalFilters());
      let changed = false;

      for (const config of additionalConfigs) {
        const valueSignal = filter.values[config.name];
        if (!valueSignal) {
          continue;
        }
        const value = valueSignal();
        if (!this.hasActiveValue(value) || active.has(config.name)) {
          continue;
        }
        // Prefer explicit add / prefs restore for builder-style filters unless
        // there are complete conditions worth showing as an active chip.
        if (
          config.skipAutoActivateWhenValuePresent &&
          !this.hasCompleteConditionGroupValue(value)
        ) {
          continue;
        }
        active.add(config.name);
        changed = true;
      }

      if (changed) {
        this.activeAdditionalFilters.set(active);
        this.renderFilterComponents();
      }
    });
  }

  ngOnDestroy(): void {
    // Flush any pending debounced save so preferences aren't lost
    if (this.saveDebounceTimer) {
      this.clearPendingSaveDebounce(false);
      this.flushPendingSave();
    }
    // Clean up all component refs
    this.componentRefs.forEach(ref => ref.destroy());
    this.componentRefs.clear();
  }

  private flushPendingSave(): void {
    const key = this.storageKey();
    if (!key || !this.savePreferencesEnabled()) {
      this.pendingSaveSessionToken = null;
      return;
    }

    const sessionNow = this.preferencesService.preferencesSessionToken(key);
    if (
      this.pendingSaveSessionToken &&
      sessionNow !== this.pendingSaveSessionToken
    ) {
      this.pendingSaveSessionToken = null;
      return;
    }

    const savedValues = this.collectPersistableValues();

    this.pendingSaveSessionToken = null;
    this.preferencesService.savePreferences(key, {
      values: savedValues,
      activeAdditionalFilters: this.persistableActiveAdditionalFilters(
        this.activeAdditionalFilters()
      ),
    });
  }

  private clearPendingSaveDebounce(discardPendingSession = true): void {
    if (this.saveDebounceTimer) {
      clearTimeout(this.saveDebounceTimer);
      this.saveDebounceTimer = null;
    }
    if (discardPendingSession) {
      this.pendingSaveSessionToken = null;
    }
  }

  toggleAdditionalDropdown(): void {
    const next = !this.showAdditionalDropdown();
    this.showAdditionalDropdown.set(next);
    if (next) {
      setTimeout(() => this.dropdownSearchInput?.nativeElement.focus());
    } else {
      this.dropdownSearch.set('');
    }
  }

  @HostListener('keydown.escape', ['$event'])
  onEscapeKey(event?: Event): void {
    if (!this.showAdditionalDropdown()) {
      return;
    }
    event?.preventDefault();
    event?.stopPropagation();
    this.showAdditionalDropdown.set(false);
    this.dropdownSearch.set('');
    setTimeout(() => this.addFilterButton?.nativeElement.focus());
  }

  addAdditionalFilter(filterName: string): void {
    const active = new Set(this.activeAdditionalFilters());
    active.add(filterName);
    this.activeAdditionalFilters.set(active);
    this.showAdditionalDropdown.set(false);
    this.pendingAutoOpen = filterName;
    this.renderFilterComponents();
  }

  removeAdditionalFilter(filterName: string): void {
    const active = new Set(this.activeAdditionalFilters());
    active.delete(filterName);
    this.activeAdditionalFilters.set(active);

    // Clear the filter value
    const valueSignal = this.filter().values[filterName];
    if (valueSignal) {
      this.resetFilterValue(valueSignal);
    }

    this.renderFilterComponents();
  }

  addAllAdditionalFilters(): void {
    const available = this.getAvailableAdditionalFilters();
    const active = new Set(this.activeAdditionalFilters());

    for (const config of available) {
      active.add(config.name);
    }

    this.activeAdditionalFilters.set(active);
    this.showAdditionalDropdown.set(false);
    this.renderFilterComponents();
  }

  removeAllAdditionalFilters(): void {
    const active = this.activeAdditionalFilters();

    // Clear all filter values
    for (const filterName of active) {
      const valueSignal = this.filter().values[filterName];
      if (valueSignal) {
        this.resetFilterValue(valueSignal);
      }
    }

    this.activeAdditionalFilters.set(new Set());
    this.showAdditionalDropdown.set(false);
    this.renderFilterComponents();
  }

  clearAllFilters(): void {
    this.clearPendingSaveDebounce();
    this.skipNextAutoSave = true;

    this.clearStoredRestoreState();
    const filter = this.filter();
    if (filter.resetToDefaults) {
      filter.resetToDefaults();
      filter.reapplyScopeDefaults?.();
    } else {
      this.resetClearableFilterValuesInMemory();
    }
    this.filterResetToken.update(token => token + 1);
    this.activeAdditionalFilters.set(new Set());
    this.showAdditionalDropdown.set(false);

    const key = this.storageKey();
    if (key) {
      this.preferencesService.clearPreferences(key);
    }

    this.renderFilterComponents();
    this.cleared.emit();
  }

  /**
   * Same in-memory reset as "clear" without touching storage. Used when the
   * logged-in user changed and there is no per-user save yet (e.g. long-lived shell).
   */
  private resetInMemoryFilterStateForUserSwitch(): void {
    this.clearPendingSaveDebounce();
    this.clearStoredRestoreState();
    this.resetClearableFilterValuesInMemory();
    this.filterResetToken.update(token => token + 1);
    this.activeAdditionalFilters.set(new Set());
    this.showAdditionalDropdown.set(false);
  }

  private resetClearableFilterValuesInMemory(): void {
    const filter = this.filter();
    const values = filter.values;
    const clearable = this.clearableFilterNames();

    for (const k of Object.keys(values)) {
      if (!clearable.has(k)) continue;
      const valueSignal = values[k];
      if (valueSignal) {
        this.resetFilterValue(valueSignal);
      }
    }

    // Some chips mirror context the user did not choose (e.g. the agency scope
    // from the app switcher). Clearing those leaves the page querying nothing,
    // so let the filter restore them.
    filter.reapplyScopeDefaults?.();
  }

  getDefaultFilterConfigs(): FilterConfig<string>[] {
    return this.filter()
      .configs()
      .filter(
        c =>
          c.location === 'default' &&
          !!c.filterComponent &&
          this.configIncluded(c.name)
      );
  }

  getAdditionalFilterConfigs(): FilterConfig<string>[] {
    return this.filter()
      .configs()
      .filter(
        c =>
          c.location === 'additional' &&
          !!c.filterComponent &&
          this.configIncluded(c.name)
      );
  }

  getAvailableAdditionalFilters(): FilterConfig<string>[] {
    const active = this.activeAdditionalFilters();
    const visible = new Set(this.visibleConfigs().map(c => c.name));

    return this.getAdditionalFilterConfigs()
      .filter(c => !active.has(c.name) && visible.has(c.name))
      .sort((a, b) => {
        const aFirst = a['moreFiltersFirst'] === true ? 0 : 1;
        const bFirst = b['moreFiltersFirst'] === true ? 0 : 1;
        if (aFirst !== bFirst) {
          return aFirst - bFirst;
        }
        const aLast = a['moreFiltersLast'] === true ? 1 : 0;
        const bLast = b['moreFiltersLast'] === true ? 1 : 0;
        if (aLast !== bLast) {
          return aLast - bLast;
        }
        const titleA = (a.title || a.name || '').toLowerCase();
        const titleB = (b.title || b.name || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });
  }

  private async loadFilterComponents(): Promise<void> {
    for (const config of this.visibleConfigs()) {
      if (config.filterComponent && !this.filterComponents[config.name]) {
        try {
          const component = await config.filterComponent();
          // The filterComponent() function already extracts .default in the config
          this.filterComponents[config.name] = component as Type<any>;
        } catch (e) {
          logger.warn(`Failed to load component for ${config.name}:`, e);
        }
      }
    }
  }

  // We need this when filter options become unavailable (e.g., no data). It will remove
  // the filters from the activeAdditionalFilters set and clear their values.
  private cleanupInvisibleActiveFilters(visibleFilterNames: Set<string>): void {
    const currentlyActiveFilterNames = this.activeAdditionalFilters();
    const filterNamesToRemove = Array.from(currentlyActiveFilterNames).filter(
      filterName =>
        !visibleFilterNames.has(filterName) &&
        // A filter the user just picked has not rendered yet; never reap it.
        filterName !== this.pendingAutoOpen
    );

    if (filterNamesToRemove.length === 0) {
      return;
    }

    const remainingActiveFilters = new Set(currentlyActiveFilterNames);
    for (const filterName of filterNamesToRemove) {
      remainingActiveFilters.delete(filterName);
    }
    this.activeAdditionalFilters.set(remainingActiveFilters);
  }

  // Additional filters are hidden by default and only shown when the user
  // manually opens them. However, when filter values are restored from query
  // params or other external sources, the filter already has a value but its
  // UI toggle hasn't been opened. This method checks every additional filter
  // and auto-opens any that already have a value so the user can see them.
  private activateFiltersWithValues(): void {
    const additionalConfigs = this.visibleConfigs().filter(
      c => c.location === 'additional'
    );
    const currentActive = this.activeAdditionalFilters();
    const updatedActive = new Set(currentActive);
    let needsUpdate = false;

    for (const config of additionalConfigs) {
      if (currentActive.has(config.name)) {
        continue;
      }
      const valueSignal = this.filter().values[config.name];
      if (!valueSignal) {
        continue;
      }
      const value = valueSignal();
      if (!this.hasActiveValue(value)) {
        continue;
      }
      if (
        config.skipAutoActivateWhenValuePresent &&
        !this.hasCompleteConditionGroupValue(value)
      ) {
        continue;
      }
      updatedActive.add(config.name);
      needsUpdate = true;
    }

    if (needsUpdate) {
      this.activeAdditionalFilters.set(updatedActive);
    }
  }

  private persistableActiveAdditionalFilters(
    active: ReadonlySet<string>
  ): string[] {
    const configsByName = new Map(
      this.filter()
        .configs()
        .map(config => [config.name, config])
    );
    const values = this.filter().values;
    const persistable = [...active].filter(name => {
      const config = configsByName.get(name);
      if (!config?.skipAutoActivateWhenValuePresent) {
        return true;
      }
      const value = values[name]?.();
      // Do not persist an empty/incomplete advanced builder as an active chip.
      return this.hasActiveValue(value);
    });

    for (const name of this.pendingRestoreActiveNames) {
      if (!configsByName.has(name) && !active.has(name)) {
        persistable.push(name);
      }
    }

    return persistable;
  }

  private collectPersistableValues(): Record<string, unknown> {
    const savedValues: Record<string, unknown> = {};
    const allValues = this.filter().values;
    const skipped = this.namesSkippingPreferences();
    for (const name of Object.keys(allValues)) {
      if (skipped.has(name)) continue;
      const valueSignal = allValues[name];
      if (valueSignal) {
        const value = valueSignal();
        if (this.hasActiveValue(value)) {
          savedValues[name] = value;
        }
      }
    }

    for (const name of this.pendingRestoreValueNames) {
      if (name in savedValues || skipped.has(name)) {
        continue;
      }
      const stored = this.restoredPreferences?.values?.[name];
      if (stored != null) {
        savedValues[name] = stored;
      }
    }

    return savedValues;
  }

  private clearStoredRestoreState(): void {
    this.restoredPreferences = null;
    this.pendingRestoreValueNames.clear();
    this.pendingRestoreActiveNames.clear();
  }

  private namesSkippingPreferences(): Set<string> {
    return new Set(
      this.filter()
        .configs()
        .filter(config => config.skipPreferences)
        .map(config => config.name)
    );
  }

  private hasActiveValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (this.isConditionGroupLike(value)) {
      return this.hasCompleteConditionGroupValue(value);
    }
    return true;
  }

  private isConditionGroupLike(value: unknown): value is {
    logicalOperator?: unknown;
    conditions?: unknown;
  } {
    return (
      typeof value === 'object' &&
      value !== null &&
      'logicalOperator' in value &&
      'conditions' in value &&
      Array.isArray((value as { conditions?: unknown }).conditions)
    );
  }

  private hasCompleteConditionGroupValue(value: unknown): boolean {
    if (!this.isConditionGroupLike(value)) {
      return false;
    }
    const stack: Array<{ conditions: unknown[] }> = [
      { conditions: value.conditions as unknown[] },
    ];
    while (stack.length > 0) {
      const current = stack.pop()!;
      for (const item of current.conditions) {
        if (!item || typeof item !== 'object') {
          continue;
        }
        const record = item as {
          condition?: {
            field?: unknown;
            operator?: unknown;
            value?: unknown;
          };
          conditionGroup?: unknown;
        };
        if (record.conditionGroup) {
          if (this.isConditionGroupLike(record.conditionGroup)) {
            stack.push({
              conditions: record.conditionGroup.conditions as unknown[],
            });
          }
          continue;
        }
        const condition = record.condition;
        if (!condition) {
          continue;
        }
        const field =
          typeof condition.field === 'string' ? condition.field.trim() : '';
        const operator =
          typeof condition.operator === 'string'
            ? condition.operator.trim()
            : '';
        if (!field || !operator) {
          continue;
        }
        if (operator === 'empty' || operator === 'notEmpty') {
          return true;
        }
        const conditionValue =
          typeof condition.value === 'string'
            ? condition.value.trim()
            : condition.value != null
              ? String(condition.value).trim()
              : '';
        if (conditionValue.length > 0) {
          return true;
        }
      }
    }
    return false;
  }

  private shouldSkipFirstAutoSave(): boolean {
    if (this.skipNextAutoSave) {
      this.skipNextAutoSave = false;
      return true;
    }

    if (this.hasSkippedFirstSave) {
      return false;
    }

    if (this.shouldAvoidPersistingIncomingFilterState()) {
      this.hasSkippedFirstSave = true;
      return true;
    }

    return false;
  }

  private resetFilterValue(valueSignal: WritableSignal<unknown>): void {
    const currentValue = valueSignal();
    if (Array.isArray(currentValue)) {
      valueSignal.set([]);
    } else if (typeof currentValue === 'string') {
      valueSignal.set('');
    } else {
      valueSignal.set(undefined);
    }
  }

  private restoreFromStorage(): void {
    const key = this.storageKey();
    if (!key) return;
    if (this.shouldUseIncomingFilterState()) return;

    const sessionToken = this.preferencesService.preferencesSessionToken(key);
    if (!sessionToken) {
      return;
    }

    if (
      this.storageLoaded() &&
      this.lastRestoredSessionToken === sessionToken
    ) {
      this.applyStoredPreferences(true);
      return;
    }

    const hadPriorUserSession = this.lastRestoredSessionToken !== null;
    const userSessionChanged =
      hadPriorUserSession && this.lastRestoredSessionToken !== sessionToken;

    // Wipe in-memory state from the previous user/context before re-applying.
    if (userSessionChanged) {
      this.resetInMemoryFilterStateForUserSwitch();
    }

    const preferences = this.preferencesService.getPreferences(key);

    this.lastRestoredSessionToken = sessionToken;
    this.storageLoaded.set(true);
    this.restoredPreferences = preferences;
    this.pendingRestoreValueNames = new Set(
      Object.keys(preferences?.values ?? {}).filter(
        name => preferences?.values?.[name] != null
      )
    );
    this.pendingRestoreActiveNames = new Set(
      preferences?.activeAdditionalFilters ?? []
    );

    if (!preferences) {
      return;
    }

    this.applyStoredPreferences(false);
  }

  /**
   * Applies stored values and active chips for every filter registered so far.
   * A `deferred` pass runs after the initial restore, once late filters (agency
   * custom-field chips) exist, so it must not overwrite what the user set since.
   */
  private applyStoredPreferences(deferred: boolean): void {
    const preferences = this.restoredPreferences;
    if (!preferences) return;
    if (
      this.pendingRestoreValueNames.size === 0 &&
      this.pendingRestoreActiveNames.size === 0
    ) {
      return;
    }

    const savedValues = preferences.values ?? {};
    const configsByName = new Map(
      this.filter()
        .configs()
        .map(config => [config.name, config])
    );

    // Restore all saved filter values (default, additional, secondary). Pages
    // that want URL or page-provided values to win should opt out via the
    // existing skip flags.
    for (const name of [...this.pendingRestoreValueNames]) {
      const config = configsByName.get(name);
      const valueSignal = this.filter().values[name];
      if (!config || !valueSignal) continue;

      this.pendingRestoreValueNames.delete(name);
      if (config.skipPreferences) {
        // A preference saved before the flag existed must not win either.
        continue;
      }

      if (
        (deferred || this.shouldAvoidPersistingIncomingFilterState()) &&
        this.hasActiveValue(valueSignal())
      ) {
        continue;
      }

      if (
        config.skipAutoActivateWhenValuePresent &&
        !this.hasActiveValue(savedValues[name])
      ) {
        // Drop stale empty/incomplete advanced builder drafts from prefs.
        continue;
      }

      valueSignal.set(savedValues[name]);
    }

    // Restore which additional filters were toggled open without hiding any
    // filters that were already activated by current values.
    const restored = new Set(this.activeAdditionalFilters());
    let changed = false;
    for (const name of [...this.pendingRestoreActiveNames]) {
      const config = configsByName.get(name);
      if (!config) continue;

      this.pendingRestoreActiveNames.delete(name);
      if (config.location !== 'additional' || restored.has(name)) {
        continue;
      }
      if (config.skipAutoActivateWhenValuePresent) {
        const value = this.filter().values[name]?.();
        // Stale prefs may list the advanced builder as active with an empty
        // or incomplete group — keep it in More Filters until needed.
        if (!this.hasActiveValue(value)) {
          continue;
        }
      }
      restored.add(name);
      changed = true;
    }
    if (changed) {
      this.activeAdditionalFilters.set(restored);
    }
  }

  private renderFilterComponents(): void {
    if (
      !this.defaultFiltersContainer ||
      !this.additionalFiltersContainer ||
      !this.secondaryFiltersContainer
    ) {
      // Wait for the containers to be available
      setTimeout(() => {
        // TODO don't call this infinitely?
        this.renderFilterComponents();
      }, 0);
      return;
    }

    this.restoreFromStorage();

    // Auto-activate additional filters that have pre-existing values
    this.activateFiltersWithValues();

    // Track which refs are still left over after rendering so we can clean them up.
    const existingRefs = new Set(this.componentRefs.keys());

    for (const config of this.visibleConfigs()) {
      const shouldRender =
        config.location === 'default' ||
        config.location === 'secondary' ||
        (config.location === 'additional' &&
          this.activeAdditionalFilters().has(config.name));

      if (!shouldRender) {
        continue;
      }

      // We're committed to rendering this filter at this point, so remove it
      // from the collection that we're going to destroy later.
      existingRefs.delete(config.name);

      const componentType = this.filterComponents[config.name];
      if (!componentType) {
        continue;
      }
      const componentInputNames = this.getComponentInputNames(componentType);
      const hasInput = (name: string): boolean =>
        componentInputNames.size === 0 || componentInputNames.has(name);

      const existingRef = this.componentRefs.get(config.name);
      if (existingRef) {
        logger.debug('Component already rendered', config.name);
        continue;
      }

      // Determine which container to use
      const targetContainer =
        config.location === 'additional'
          ? this.additionalFiltersContainer
          : config.location === 'secondary'
            ? this.secondaryFiltersContainer
            : this.defaultFiltersContainer;

      // Create new component
      try {
        const bindings = [
          // TODO if there is no value signal, do we skip the component?
          twoWayBinding('value', this.filter().values[config.name]),
        ];

        // Ensure options signal exists so components with required models can bind immediately.
        let optionsSignal = this.options[config.name];
        if (!optionsSignal) {
          // Seed from the config's own options; otherwise an empty array so
          // required models don't fail.
          optionsSignal = signal<unknown>(config['options'] ?? []);
          this.options[config.name] = optionsSignal;
        } else if (
          config['options'] &&
          !untracked(() => this.optionsQuery.data())?.[config.name]
        ) {
          // A re-added filter reuses its signal; its config may have changed.
          optionsSignal.set(config['options']);
        }
        if (hasInput('options')) {
          bindings.push(twoWayBinding('options', optionsSignal));
        }
        if (hasInput('resetToken')) {
          bindings.push(
            inputBinding('resetToken', () => this.filterResetToken())
          );
        }

        const filterName = config.name;
        for (const [key, value] of Object.entries(config)) {
          if (
            key === 'filterComponent' ||
            key === 'name' ||
            key === 'location' ||
            // Bound through the shared options signal above.
            key === 'options' ||
            key === 'skipAutoActivateWhenValuePresent' ||
            key === 'moreFiltersFirst' ||
            key === 'moreFiltersLast' ||
            key === 'showWhenOptionsEmpty' ||
            key === 'icon' ||
            key === 'iconClass' ||
            key === 'required'
          ) {
            continue;
          }
          if (!hasInput(key)) {
            continue;
          }
          // Re-read from live configs() so callbacks like
          // onDropdownOpenChange stay current after staged-apply enables.
          if (key === 'onDropdownOpenChange') {
            bindings.push(
              inputBinding(key, () => {
                const live = this.filter()
                  .configs()
                  .find(c => c.name === filterName) as
                  Record<string, unknown> | undefined;
                return live?.['onDropdownOpenChange'];
              })
            );
            continue;
          }
          // Bind a config's custom properties
          bindings.push(inputBinding(key, () => value));
        }

        // Set up additional filters so that they can be removed
        if (config.location === 'additional' && hasInput('onRemove')) {
          bindings.push(
            inputBinding('onRemove', () => {
              return () => this.removeAdditionalFilter(config.name);
            })
          );
        }

        // Escape overflow scroll parents (new sidebar content pane) so menus
        // and focus outlines aren't clipped by the white card.
        if (
          hasInput('fixedDropdown') &&
          !Object.prototype.hasOwnProperty.call(config, 'fixedDropdown')
        ) {
          bindings.push(inputBinding('fixedDropdown', () => true));
        }

        const componentRef = targetContainer!.createComponent(componentType, {
          environmentInjector: this.envInjector,
          bindings,
        });

        logger.debug(
          'Rendered filter component with initial value',
          config.name,
          this.filter().values[config.name]()
        );

        this.componentRefs.set(config.name, componentRef);

        // Auto-open the dropdown for an added additional filter
        if (this.pendingAutoOpen === config.name) {
          this.pendingAutoOpen = null;
          setTimeout(() => {
            if (
              componentRef.instance &&
              typeof componentRef.instance.toggleDropdown === 'function'
            ) {
              componentRef.instance.toggleDropdown();
            }
          });
        }
      } catch (e) {
        logger.warn(`Failed to create component for ${config.name}:`, e);
      }
    }

    for (const existingRefKey of existingRefs) {
      const existingRef = this.componentRefs.get(existingRefKey);
      // Destroy components that shouldn't be rendered anymore
      if (existingRef) {
        existingRef.destroy();
        this.componentRefs.delete(existingRefKey);
        logger.debug('Destroyed filter component', existingRefKey);
      }
    }
  }

  private getComponentInputNames(componentType: Type<any>): Set<string> {
    const names = new Set<string>();
    const componentDefinition = (componentType as { ɵcmp?: { inputs?: any } })
      .ɵcmp;
    const inputs = componentDefinition?.inputs;
    if (!inputs || typeof inputs !== 'object') {
      return names;
    }

    for (const [key, value] of Object.entries(inputs)) {
      names.add(key);
      if (typeof value === 'string') {
        names.add(value);
        continue;
      }
      if (Array.isArray(value)) {
        for (const entry of value) {
          if (typeof entry === 'string') {
            names.add(entry);
          }
        }
        continue;
      }
      if (value && typeof value === 'object') {
        for (const entry of Object.values(value)) {
          if (typeof entry === 'string') {
            names.add(entry);
          }
        }
      }
    }

    return names;
  }
}
